import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Star, MapPin, Filter } from "lucide-react";
import { browsePriests } from "../lib/api";

const LANGS = ["All", "Kannada", "Sanskrit", "Hindi", "Marathi", "Tamil", "Telugu", "English"];
const AREAS = ["All", "Jayanagar", "JP Nagar", "Rajajinagar", "Malleshwaram", "Banashankari", "Marathahalli"];
const PRICES = [
  { label: "All", min: null, max: null },
  { label: "₹100 - ₹5K", min: 100, max: 5000 },
  { label: "₹5K - ₹15K", min: 5000, max: 15000 },
  { label: "₹15K - ₹50K", min: 15000, max: 50000 },
];

export default function Priests() {
  const [params] = useSearchParams();
  const poojaSlug = params.get("poojaSlug") || "";
  const poojaName = params.get("poojaName") || "Purohits";
  const [lang, setLang] = useState("All");
  const [area, setArea] = useState("All");
  const [price, setPrice] = useState(0);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    setLoading(true);
    const p = PRICES[price];
    browsePriests({
      poojaSlug: poojaSlug || undefined,
      language: lang !== "All" ? lang : undefined,
      area: area !== "All" ? area : undefined,
      minPrice: p.min ?? undefined,
      maxPrice: p.max ?? undefined,
    }).then((d) => setList(d.priests)).finally(() => setLoading(false));
  }, [poojaSlug, lang, area, price]);

  const Chip = ({ active, onClick, children, testid }) => (
    <button onClick={onClick} data-testid={testid}
      className={`rounded-full px-3 py-1 text-xs font-medium ${active ? "bg-[#8B1A2B] text-white" : "border border-[#E7DCCB] bg-white text-[#5C5149]"}`}>{children}</button>
  );

  return (
    <div className="px-4 pt-5 pc-fade-in" data-testid="cust-priests">
      <p className="text-xs text-[#A0968A]"><Link to="/app" className="hover:text-[#8B1A2B]">Home</Link> / Purohits</p>
      <div className="mt-1 flex items-center justify-between">
        <h1 className="font-heading text-2xl text-[#2A211C]">{poojaName}</h1>
        <button onClick={() => setShowFilters((s) => !s)} data-testid="cust-toggle-filters"
          className="flex items-center gap-1.5 rounded-full border border-[#E7DCCB] bg-white px-3 py-1.5 text-xs font-semibold text-[#5C5149]">
          <Filter size={14} /> Filters
        </button>
      </div>
      <p className="mt-0.5 text-sm text-[#7A6E64]">{loading ? "Loading…" : `${list.length} profiles`}</p>

      {showFilters && (
        <div className="mt-3 space-y-3 rounded-2xl border border-[#EBE0D0] bg-white p-4" data-testid="cust-filters">
          <div>
            <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#A0968A]">Languages</p>
            <div className="flex flex-wrap gap-1.5">{LANGS.map((l) => <Chip key={l} active={lang === l} onClick={() => setLang(l)} testid={`filter-lang-${l}`}>{l}</Chip>)}</div>
          </div>
          <div>
            <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#A0968A]">Service area</p>
            <div className="flex flex-wrap gap-1.5">{AREAS.map((a) => <Chip key={a} active={area === a} onClick={() => setArea(a)} testid={`filter-area-${a}`}>{a}</Chip>)}</div>
          </div>
          <div>
            <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#A0968A]">Price range</p>
            <div className="flex flex-wrap gap-1.5">{PRICES.map((p, i) => <Chip key={p.label} active={price === i} onClick={() => setPrice(i)} testid={`filter-price-${i}`}>{p.label}</Chip>)}</div>
          </div>
        </div>
      )}

      <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3 pc-stagger">
        {!loading && list.length === 0 && <p className="rounded-xl border border-[#EBE0D0] bg-white p-6 text-center text-sm text-[#7A6E64] sm:col-span-2 xl:col-span-3">No purohits match these filters.</p>}
        {list.map((p) => (
          <div key={p.id} data-testid={`cust-priest-${p.id}`} className="pc-card-hover flex flex-col rounded-2xl border border-[#EBE0D0] bg-white p-4">
            <div className="flex items-start gap-3">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#F5E9DA] font-heading text-lg text-[#8B1A2B]">{p.name[0]?.toUpperCase()}</span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between">
                  <h3 className="font-heading text-lg text-[#2A211C]">{p.name}</h3>
                  <span className="flex items-center gap-1 text-xs text-[#7A6E64]"><Star size={13} className="fill-[#E8A200] text-[#E8A200]" /> {p.rating.toFixed(1)} · {p.reviews_count}</span>
                </div>
                <p className="text-xs text-[#B58A5E]">{p.title} · {p.experience_years}+ yrs</p>
                <p className="mt-1 text-sm font-semibold text-[#2A211C]">₹{p.price_min.toLocaleString("en-IN")} - ₹{p.price_max.toLocaleString("en-IN")}</p>
                <p className="mt-1 text-xs text-[#7A6E64]">{p.languages.join(", ")}</p>
                <p className="mt-1 flex items-center gap-1 text-xs text-[#7A6E64]"><MapPin size={12} /> {p.areas.slice(0, 4).join(" · ")}</p>
              </div>
            </div>
            <Link to={`/app/request?poojaSlug=${poojaSlug}&poojaName=${encodeURIComponent(poojaName)}`} data-testid={`cust-priest-request-${p.id}`}
              className="mt-3 block rounded-xl bg-[#8B1A2B] py-2.5 text-center text-sm font-semibold text-white transition active:scale-[0.98] hover:bg-[#6F1420]">Request this purohit</Link>
          </div>
        ))}
      </div>
    </div>
  );
}
